import { useState, useRef, useEffect } from "react";
import { uploadCsv, syncFromHesttya } from "../lib/api";
import { UploadSimple, FileCsv, CheckCircle, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useSnapshot } from "../contexts/SnapshotContext";
import { useAuth } from "../contexts/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

function defaultLabel() {
  const now = new Date();
  const mes = now.toLocaleDateString("es-ES", { month: "long" });
  return `Seguimiento ${mes} ${now.getFullYear()}`;
}

export default function SubirCSV() {
  const { refresh } = useSnapshot();
  const { isSuperadmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isSuperadmin) navigate("/", { replace: true });
  }, [isSuperadmin, navigate]);

  const [file, setFile] = useState(null);
  const [period, setPeriod] = useState("");
  const [label, setLabel] = useState(defaultLabel);
  const [snapshotDate, setSnapshotDate] = useState("");
  const [uploading, setUploading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMonth, setSyncMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [syncStartDate, setSyncStartDate] = useState("");
  const [syncEndDate, setSyncEndDate] = useState("");
  const [customSyncRange, setCustomSyncRange] = useState(false);
  const [result, setResult] = useState(null);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef();

  useEffect(() => {
    if (!syncMonth) return;
    const [y, m] = syncMonth.split("-").map(Number);
    const start = `${y}-${String(m).padStart(2, "0")}-01`;
    
    const now = new Date();
    const currentPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    let end;
    if (syncMonth === currentPeriod) {
      end = now.toISOString().slice(0, 10);
    } else {
      const lastDay = new Date(y, m, 0).getDate();
      end = `${y}-${String(m).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
    }
    setSyncStartDate(start);
    setSyncEndDate(end);
  }, [syncMonth]);


  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".csv")) {
      toast.error("El archivo debe ser un .csv");
      return;
    }
    setFile(f);
    // Try detect period from name
    const m = f.name.match(/(\d{4}-\d{2})-\d{2}/);
    if (m) setPeriod(m[1]);
    // Default snapshot date = today
    const today = new Date().toISOString().slice(0, 10);
    setSnapshotDate(today);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const handleSync = async () => {
    const [y, m] = syncMonth.split("-").map(Number);
    setSyncing(true);
    try {
      const r = await syncFromHesttya(
        y,
        m,
        customSyncRange ? syncStartDate : undefined,
        customSyncRange ? syncEndDate : undefined
      );
      toast.success(`Sincronizado · ${r.count} consultoras · ${r.label}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al sincronizar con Hesttya");
    } finally {
      setSyncing(false);
    }
  };

  const submit = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const r = await uploadCsv(file, period, label, snapshotDate);
      setResult(r);
      toast.success(`CSV procesado · ${r.count} consultoras · ${r.snapshot_date}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al procesar el CSV");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-3xl space-y-6" data-testid="subir-page">
      <header>
        <p className="text-xs tracking-widest uppercase text-[#736F68] font-display font-semibold">
          Carga de datos
        </p>
        <h1 className="mt-1 text-3xl md:text-4xl font-semibold font-display text-[#2D2A26]">
          Subir CSV mensual
        </h1>
        <p className="mt-1 text-[#736F68] text-sm">
          Sube el archivo de tu equipo Hesttya. Si ya existe un snapshot del mismo periodo, se sustituirá.
        </p>
      </header>

      {!result && (
        <>
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDrag(true);
            }}
            onDragLeave={() => setDrag(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all ${
              drag
                ? "border-[#C57B67] bg-[#C57B67]/5"
                : "border-[#E5E0D8] bg-white hover:border-[#C57B67]/50 hover:bg-[#F9F8F6]"
            }`}
            data-testid="dropzone"
          >
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="file-input"
            />
            {file ? (
              <div className="flex items-center justify-center gap-3 text-[#2D2A26]">
                <FileCsv size={32} weight="duotone" className="text-[#C57B67]" />
                <div className="text-left">
                  <p className="font-medium">{file.name}</p>
                  <p className="text-xs text-[#736F68]">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3 text-[#736F68]">
                <div className="w-14 h-14 rounded-2xl bg-[#F2EFEA] flex items-center justify-center">
                  <UploadSimple size={28} weight="duotone" className="text-[#C57B67]" />
                </div>
                <p className="font-medium text-[#2D2A26]">
                  Arrastra tu archivo CSV o pulsa para seleccionar
                </p>
                <p className="text-xs">
                  Formato esperado: el CSV oficial de Hesttya (semicolon, decimales con coma)
                </p>
              </div>
            )}
          </div>

          {file && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-[#736F68] font-medium">
                  Periodo (YYYY-MM)
                </Label>
                <Input
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  placeholder="2026-04"
                  data-testid="period-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#736F68] font-medium">
                  Fecha del snapshot
                </Label>
                <Input
                  type="date"
                  value={snapshotDate}
                  onChange={(e) => setSnapshotDate(e.target.value)}
                  data-testid="snapshot-date-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#736F68] font-medium">
                  Etiqueta (opcional)
                </Label>
                <Input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="Cierre abril 2026"
                  data-testid="label-input"
                />
              </div>
            </div>
          )}

          {file && (
            <div className="flex gap-3">
              <button
                onClick={submit}
                disabled={uploading}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#C57B67] hover:bg-[#B06A57] text-white font-medium disabled:opacity-50 transition-colors"
                data-testid="upload-submit-btn"
              >
                {uploading ? "Procesando…" : "Procesar archivo"}
              </button>
              <button
                onClick={() => {
                  setFile(null);
                  setPeriod("");
                  setLabel(defaultLabel());
                }}
                className="px-4 py-3 rounded-lg border border-[#E5E0D8] text-[#2D2A26] hover:bg-[#F2EFEA]"
              >
                Quitar
              </button>
            </div>
          )}
        </>
      )}

      {result && (
        <div className="bg-white border border-[#E5E0D8] rounded-xl p-8 text-center space-y-4" data-testid="upload-success">
          <CheckCircle size={56} weight="duotone" className="mx-auto text-[#8A9A86]" />
          <div>
            <p className="text-xl font-semibold font-display text-[#2D2A26]">
              ¡CSV procesado correctamente!
            </p>
            <p className="text-sm text-[#736F68] mt-1">
              Periodo {result.period} · {result.count} consultoras · Coordinadora{" "}
              {result.coordenador || "—"}
            </p>
          </div>
          <div className="flex gap-3 justify-center pt-2">
            <button
              onClick={() => navigate("/")}
              className="px-4 py-2.5 rounded-lg bg-[#C57B67] hover:bg-[#B06A57] text-white font-medium transition-colors"
              data-testid="goto-dashboard-btn"
            >
              Ver panel
            </button>
            <button
              onClick={() => {
                setResult(null);
                setFile(null);
                setPeriod("");
                setLabel("");
              }}
              className="px-4 py-2.5 rounded-lg border border-[#E5E0D8] text-[#2D2A26] hover:bg-[#F2EFEA]"
            >
              Subir otro
            </button>
          </div>
        </div>
      )}

      {/* Sincronización automática desde Hesttya */}
      <div className="bg-white border border-[#E5E0D8] rounded-xl p-5 space-y-4">
        <div>
          <p className="font-semibold text-[#2D2A26] text-sm flex items-center gap-2">
            <ArrowsClockwise size={15} weight="bold" className="text-[#C57B67]" />
            Sincronizar desde Hesttya
          </p>
          <p className="text-xs text-[#736F68] mt-0.5">
            Descarga el CSV directamente de <span className="font-mono">app.hesttya.pt</span> sin necesidad de exportar manualmente.
          </p>
        </div>
        <div className="space-y-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="space-y-1">
              <Label className="text-xs text-[#736F68] font-medium">Mes / Periodo</Label>
              <input
                type="month"
                value={syncMonth}
                onChange={(e) => setSyncMonth(e.target.value)}
                className="text-sm border border-[#E5E0D8] rounded-lg px-2.5 py-2 bg-[#F9F8F6] focus:outline-none focus:ring-1 focus:ring-[#C57B67]"
              />
            </div>
            
            <div className="flex items-center gap-2 mt-5">
              <input
                type="checkbox"
                id="custom-range"
                checked={customSyncRange}
                onChange={(e) => setCustomSyncRange(e.target.checked)}
                className="rounded border-[#E5E0D8] text-[#C57B67] focus:ring-[#C57B67]"
              />
              <Label htmlFor="custom-range" className="text-xs font-medium text-[#2D2A26] cursor-pointer">
                Rango de fechas personalizado (periodo completo)
              </Label>
            </div>
          </div>

          {customSyncRange && (
            <div className="grid grid-cols-2 gap-4 max-w-md p-3 bg-[#F9F8F6] border border-[#E5E0D8] rounded-lg">
              <div className="space-y-1">
                <Label className="text-xs text-[#736F68] font-medium">Fecha Inicio</Label>
                <input
                  type="date"
                  value={syncStartDate}
                  onChange={(e) => setSyncStartDate(e.target.value)}
                  className="text-xs w-full border border-[#E5E0D8] rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-[#C57B67]"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-[#736F68] font-medium">Fecha Fin</Label>
                <input
                  type="date"
                  value={syncEndDate}
                  onChange={(e) => setSyncEndDate(e.target.value)}
                  className="text-xs w-full border border-[#E5E0D8] rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-[#C57B67]"
                />
              </div>
            </div>
          )}

          <div>
            <button
              onClick={handleSync}
              disabled={syncing}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#2D2A26] hover:bg-[#1a1816] text-white text-sm font-medium disabled:opacity-50 transition-colors"
            >
              <ArrowsClockwise size={14} weight="bold" className={syncing ? "animate-spin" : ""} />
              {syncing ? "Sincronizando…" : "Sincronizar"}
            </button>
          </div>
        </div>
      </div>


      <div className="bg-[#F2EFEA]/60 border border-[#E5E0D8] rounded-xl p-5 text-sm text-[#736F68]">
        <p className="font-semibold text-[#2D2A26] mb-1">Cálculos automáticos</p>
        <ul className="space-y-1 list-disc pl-5">
          <li>Margen Consultora 25% sobre PVP</li>
          <li>Comisión Júnior 4% (a partir de 1.650€ equipo y 4 directos activos)</li>
          <li>Comisión Ejecutiva 6/7/8% por tramos (4.950€ / 8.250€ / 13.200€)</li>
          <li>Master 4% adicional sobre estructura LE directa</li>
        </ul>
      </div>
    </div>
  );
}